import classNames from "classnames";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../ui-kit/kuika-tooltip/kuika-tooltip";
import BoardAvatar from "./board-avatar";
import "./board-list-view.scss";
import { buildBoardPath } from "./board-routing";
import { BoardService } from "./board-service";
import { confirmDialog } from "./confirm-service";
import { currentUserId } from "./current-user";
import { displayNameOf } from "./people-directory";
import TypeIcon from "./type-icon";
import { Board, BoardStatus, IssueCard, IssuePriority, PRIORITY_RANK, Project, STATUS_DOT_COLOR } from "./types";
import { ColumnFilters, useColumnIssues } from "./use-column-issues";

export interface BoardListViewProps {
  board: Board;
  boardId: string;
  filters: ColumnFilters;
  statuses: BoardStatus[];
  projects: Project[];
  showProjectColumn: boolean;
}

type SortKey = "key" | "summary" | "status" | "priority" | "assignee" | "updated";

const BoardListView: React.FC<BoardListViewProps> = ({
  board,
  boardId,
  filters,
  statuses,
  projects,
  showProjectColumn
}) => {
  const navigate = useNavigate();
  const { domainName } = useParams<{ domainName: string }>();
  const [sortKey, setSortKey] = useState<SortKey>("updated");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<string>("");

  // Board-wide server query (statusId omitted spans every column); filters are applied
  // server-side. Sorting is client-side over the loaded rows; scrolling loads more.
  const { items, hasMore, loading, loadMore } = useColumnIssues(boardId, "", [], filters);

  const sentinelRef = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    const sentinel: HTMLTableRowElement | null = sentinelRef.current;
    if (!sentinel || !hasMore) return;
    const observer: IntersectionObserver = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        if (entries.some((entry: IntersectionObserverEntry) => entry.isIntersecting)) loadMore();
      },
      { rootMargin: "300px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  const filtered: IssueCard[] = useMemo(() => {
    const list: IssueCard[] = items.filter((issue: IssueCard) => !issue.parentIssueId);
    const getStatusOrder = (statusId: string): number =>
      statuses.findIndex((entry: BoardStatus) => entry.id === statusId);
    list.sort((a: IssueCard, b: IssueCard) => {
      let result: number = 0;
      switch (sortKey) {
        case "key":
          result = a.key.localeCompare(b.key, undefined, { numeric: true });
          break;
        case "summary":
          result = a.summary.localeCompare(b.summary);
          break;
        case "status":
          result = getStatusOrder(a.statusId) - getStatusOrder(b.statusId);
          break;
        case "priority":
          result = PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
          break;
        case "assignee":
          result = displayNameOf(a.assigneeUserId).localeCompare(displayNameOf(b.assigneeUserId));
          break;
        case "updated":
          result = a.updatedAt.localeCompare(b.updatedAt);
          break;
      }
      return sortDir === "asc" ? result : -result;
    });
    return list;
  }, [items, statuses, sortKey, sortDir]);

  const sortClick = (key: SortKey): void => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir(key === "updated" ? "desc" : "asc");
    }
  };

  const toggleAll = (): void => {
    if (selected.size === filtered.length && filtered.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((issue: IssueCard) => issue.id)));
    }
  };

  const toggleOne = (id: string): void => {
    const next: Set<string> = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const bulkSetStatus = (statusId: string): void => {
    for (const id of selected) {
      const source: IssueCard | undefined = items.find((issue: IssueCard) => issue.id === id);
      BoardService.moveIssue({
        issueId: id,
        destinationStatusId: statusId,
        destinationIndex: 0,
        sourceStatusId: source?.statusId
      });
    }
  };

  const bulkSetPriority = (priority: IssuePriority): void => {
    for (const id of selected) {
      BoardService.updateIssue(id, { priority });
    }
  };

  const bulkAssignToMe = (): void => {
    for (const id of selected) {
      BoardService.updateIssue(id, { assigneeUserId: currentUserId() });
    }
  };

  const bulkDelete = (): void => {
    void confirmDialog({
      title: "Delete issues",
      message: `Delete ${selected.size} issues? This can't be undone.`,
      confirmLabel: "Delete",
      danger: true
    }).then((ok: boolean) => {
      if (!ok) return;
      for (const id of selected) {
        BoardService.deleteIssue(id);
      }
      setSelected(new Set());
    });
  };

  const allSelected: boolean = filtered.length > 0 && selected.size === filtered.length;
  const someSelected: boolean = selected.size > 0 && selected.size < filtered.length;

  return (
    <div className="kuika-board-list">
      {selected.size > 0 && (
        <div className="kuika-board-list__bulk-bar">
          <span className="kuika-board-list__bulk-count">{selected.size} selected</span>
          <span className="kuika-board-list__bulk-divider" />
          <span className="kuika-board-list__bulk-label">Set status:</span>
          {statuses.map((status: BoardStatus) => (
            <button
              key={status.id}
              type="button"
              className="kuika-board-list__bulk-status"
              onClick={() => bulkSetStatus(status.id)}
            >
              <span
                className="kuika-board-list__status-dot"
                style={{ background: STATUS_DOT_COLOR[status.category] ?? "var(--dark-4)" }}
              />
              {status.name}
            </button>
          ))}
          <span className="kuika-board-list__bulk-divider" />
          <span className="kuika-board-list__bulk-label">Priority:</span>
          <select
            className="kuika-board-list__bulk-select"
            defaultValue=""
            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
              if (event.target.value) bulkSetPriority(event.target.value as IssuePriority);
              event.target.value = "";
            }}
            aria-label="Set priority on selected issues"
          >
            <option value="" disabled>
              Set…
            </option>
            {(Object.keys(PRIORITY_RANK) as IssuePriority[]).map((priority: IssuePriority) => (
              <option key={priority} value={priority}>
                {priority}
              </option>
            ))}
          </select>
          <KuikaButton type="tertiary" color="neutral" size="sm" onClick={bulkAssignToMe}>
            Assign to me
          </KuikaButton>
          <span className="kuika-board-list__bulk-spacer" />
          <KuikaTooltip content="Delete selected" position="bottom">
            <KuikaButton type="tertiary" color="danger" size="sm" isIcon onClick={bulkDelete}>
              <KuikaSvg name="trash" width={14} height={14} />
            </KuikaButton>
          </KuikaTooltip>
          <KuikaButton type="tertiary" color="neutral" size="sm" onClick={() => setSelected(new Set())}>
            Clear
          </KuikaButton>
        </div>
      )}
      <div className="kuika-board-list__scroll">
        <table className="kuika-board-list__table">
          <thead>
            <tr>
              <th className="kuika-board-list__col-check">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(node: HTMLInputElement | null) => {
                    if (node) node.indeterminate = someSelected;
                  }}
                  onChange={toggleAll}
                  aria-label="Select all"
                />
              </th>
              <th className="kuika-board-list__col-key">
                <button type="button" onClick={() => sortClick("key")}>
                  Key {sortKey === "key" && (sortDir === "asc" ? "↑" : "↓")}
                </button>
              </th>
              <th>
                <button type="button" onClick={() => sortClick("summary")}>
                  Title {sortKey === "summary" && (sortDir === "asc" ? "↑" : "↓")}
                </button>
              </th>
              {showProjectColumn && <th className="kuika-board-list__col-project">Project</th>}
              <th className="kuika-board-list__col-status">
                <button type="button" onClick={() => sortClick("status")}>
                  Status {sortKey === "status" && (sortDir === "asc" ? "↑" : "↓")}
                </button>
              </th>
              <th className="kuika-board-list__col-priority">
                <button type="button" onClick={() => sortClick("priority")}>
                  Priority {sortKey === "priority" && (sortDir === "asc" ? "↑" : "↓")}
                </button>
              </th>
              <th className="kuika-board-list__col-assignee">
                <button type="button" onClick={() => sortClick("assignee")}>
                  Assignee {sortKey === "assignee" && (sortDir === "asc" ? "↑" : "↓")}
                </button>
              </th>
              <th className="kuika-board-list__col-updated">
                <button type="button" onClick={() => sortClick("updated")}>
                  Updated {sortKey === "updated" && (sortDir === "asc" ? "↑" : "↓")}
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={showProjectColumn ? 8 : 7} className="kuika-board-list__empty">
                  No issues match.
                </td>
              </tr>
            )}
            {filtered.map((issue: IssueCard) => {
              const status: BoardStatus | undefined = statuses.find((s: BoardStatus) => s.id === issue.statusId);
              const project: Project | undefined = projects.find((p: Project) => p.key === issue.projectKey);
              const isSelected: boolean = selected.has(issue.id);
              return (
                <tr
                  key={issue.id}
                  className={classNames("kuika-board-list__row", { "kuika-board-list__row--selected": isSelected })}
                  onClick={() => navigate(`${buildBoardPath(domainName ?? "", board)}/${issue.key}`)}
                >
                  <td className="kuika-board-list__col-check" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleOne(issue.id)}
                      aria-label={`Select ${issue.key}`}
                    />
                  </td>
                  <td className="kuika-board-list__col-key">
                    <span className="kuika-board-list__key">{issue.key}</span>
                  </td>
                  <td className="kuika-board-list__col-summary">
                    <TypeIcon type={issue.type} />
                    {editingId === issue.id ? (
                      <input
                        className="kuika-board-list__summary-input"
                        value={editingValue}
                        autoFocus
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setEditingValue(event.target.value)}
                        onClick={(event: React.MouseEvent) => event.stopPropagation()}
                        onBlur={() => {
                          const trimmed: string = editingValue.trim();
                          if (trimmed && trimmed !== issue.summary) {
                            BoardService.updateIssue(issue.id, { summary: trimmed });
                          }
                          setEditingId(null);
                        }}
                        onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            (event.target as HTMLInputElement).blur();
                          } else if (event.key === "Escape") {
                            event.preventDefault();
                            setEditingId(null);
                          }
                        }}
                      />
                    ) : (
                      <span
                        className="kuika-board-list__summary"
                        onClick={(event: React.MouseEvent) => {
                          event.stopPropagation();
                          setEditingId(issue.id);
                          setEditingValue(issue.summary);
                        }}
                      >
                        {issue.summary}
                      </span>
                    )}
                  </td>
                  {showProjectColumn && (
                    <td className="kuika-board-list__col-project">
                      {project && (
                        <span className="kuika-board-list__project">
                          <span
                            className="kuika-board-list__project-dot"
                            style={{ background: project.color }}
                            aria-hidden="true"
                          />
                          {project.key}
                        </span>
                      )}
                    </td>
                  )}
                  <td className="kuika-board-list__col-status">
                    <span className="kuika-board-list__status">
                      <span
                        className="kuika-board-list__status-dot"
                        style={{ background: STATUS_DOT_COLOR[status?.category ?? "todo"] }}
                        aria-hidden="true"
                      />
                      {status?.name ?? "—"}
                    </span>
                  </td>
                  <td className="kuika-board-list__col-priority">
                    <span
                      className={`kuika-board__priority kuika-board__priority--${issue.priority}`}
                      aria-label={issue.priority}
                    >
                      <span />
                      <span />
                      <span />
                    </span>
                  </td>
                  <td className="kuika-board-list__col-assignee">
                    {issue.assigneeUserId ? (
                      <span className="kuika-board-list__assignee">
                        <BoardAvatar
                          userId={issue.assigneeUserId}
                          className="kuika-board__assignee-avatar"
                          ariaHidden
                        />
                        {displayNameOf(issue.assigneeUserId)}
                      </span>
                    ) : (
                      <span className="kuika-board-list__unassigned">—</span>
                    )}
                  </td>
                  <td className="kuika-board-list__col-updated">
                    <span className="kuika-board-list__updated">{dayjs(issue.updatedAt).format("MMM D")}</span>
                  </td>
                </tr>
              );
            })}
            {(hasMore || loading) && (
              <tr ref={sentinelRef} className="kuika-board-list__sentinel">
                <td colSpan={showProjectColumn ? 8 : 7}>
                  {loading && (
                    <div className="kuika-board__column-loading-more" role="status" aria-label="Loading more issues">
                      <span className="kuika-board__column-loading-bar" />
                    </div>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BoardListView;
